import { useState, useEffect } from "react";
import { SortableContainer, SortableContainerProps, SortableElement, SortableElementProps } from "react-sortable-hoc";
import { arrayMoveImmutable } from "array-move";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { Button } from "@cofano/cck";
import { CustomDialog } from "components/dialogs/Dialogs";
import { Language } from "imports/Utils";
import { SaveStatusesOrderDocument, SaveStatusesOrderMutation } from "__generated__/view/statuses/graphql/mutations.generated";
import { StatusFragmentFragment as StatusFragment } from "__generated__/view/statuses/graphql/fragments.generated";
import { useTheme } from "@mui/material";

type ChangeStatusOrderDialogProps = {
	open: boolean,
	onClose: () => void,
	statuses?: StatusFragment[],
	refetch: () => void;
}

const ChangeStatusOrderDialog = ({ open, onClose, statuses, refetch }: ChangeStatusOrderDialogProps) => {

	const [ list, setList ] = useState<StatusFragment[]>([]);

	const theme = useTheme();

	const [ saveOrders, { loading } ] = useMutation<SaveStatusesOrderMutation>(SaveStatusesOrderDocument);

	const handleOnClose = () => {
		onClose();
		setList([]);
	};

	const save = () => {
		saveOrders({
			variables: {
				inputs: [ ...list ].map((status, index) => ({
					id: status.id,
					statusOrder: index,
				})),
			},
		}).then(() => {
			handleOnClose();
			refetch();
		});
	};

	const SortableItem: React.ComponentClass<SortableElementProps & { item: StatusFragment }> = (
		SortableElement(({ item }: { item: StatusFragment }) => (
			<div
				className="sortable-item"
				style={{
					backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#FFF",
					borderBottom: theme.palette.mode === "dark" ? "1px solid rgba(0, 0, 0, 0.87)" : "1px solid #F0F0F0",
					color: theme.palette.mode === "dark" ? "#fff" : "#333",
				}}>
				<span style={{ backgroundColor: item.color }} />
				{item.description}
			</div>
		))
	);

	const SortableList: React.ComponentClass<SortableContainerProps & { items: StatusFragment[] }> = (
		SortableContainer(({ items }: { items: StatusFragment[] }) => (
			<div
				className="sortable-list"
				style={{
					backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#F3F3F3",
					border: theme.palette.mode === "dark" ? "1px solid rgba(0, 0, 0, 0.87)" : "1px solid #E5E5E5",
				}}>
				{items.map((item, index) => (
					<SortableItem key={`item-${item.id}`} index={index} item={item} />
				))}
			</div>
		))
	);

	const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
		setList(arrayMoveImmutable([ ...list ], oldIndex, newIndex));
	};

	useEffect(() => {
		if (open && statuses) {
			const sortedStatuses = [ ...statuses ].sort((x, y) => {
				const orderX = x.statusOrder ?? Number.MAX_SAFE_INTEGER;
				const orderY = y.statusOrder ?? Number.MAX_SAFE_INTEGER;
				return orderX - orderY;
			});
			setList(sortedStatuses);
		}
	}, [ open, statuses ]);

	return (
		<CustomDialog
			open={open}
			title={Language("CHANGE_ORDER")}
			onClose={() => handleOnClose()}
			maxWidth="sm"
			dialogStyling={{ content: { backgroundColor: theme.palette.mode === "dark" ? "inherit" : "#EEE" } }}
			actions={(
				<Button
					loading={loading}
					onClick={() => save()}
					size="small">
					{intl.formatMessage({ id: "SAVE" })}
				</Button>
			)}>
			<div className="change-status-order-dialog">
				<SortableList
					items={list}
					onSortEnd={onSortEnd}
					helperClass={"sortable-status-helper"} />
			</div>
		</CustomDialog>
	);
};

export default ChangeStatusOrderDialog;
